// React
import { useState, Fragment } from "react"

// Translations
import { useTrans } from "@/i18n"

// Assets
import BrandLogo from "@/assets/icons/brand/zonhub_logo.svg?react"

// UI
import { cn } from "@/lib/utils"
import { ButtonLink } from "@/components/Button"
import { Dialog, DialogBackdrop, Transition, TransitionChild } from "@/lib/ui"

// Icons
import { MenuIcon } from "@/components/MenuIcon"

// Router
import { useLocation } from "@/lib/router"

// Environment
import { API_URL, getLoginUrl } from "@/lib/env"

// API
import { useApiCmsNavigationList } from "@/api/rest/generated/api/api"
import { NavigationItemType } from "@/api/rest/generated/@types/navigationItemType"

// Django's Tailgrids theme uses `text-body-color` (#637381) for sub-menu link
// text. The SPA's Tailwind config doesn't define that token, so we mirror the
// exact hex here.
const BODY_COLOR_CLASS = "text-[#637381]"

const SIGNUP_URL = `${API_URL}account/register/`

/**
 * HeaderNoAuth
 * @returns
 */
export const HeaderNoAuth = () => {
	const t = useTrans("common")
	// Re-renders on navigation, so the login links return to the current page
	useLocation()
	const loginUrl = getLoginUrl(window.location.href)

	// Query
	const { data: navigation } = useApiCmsNavigationList()

	// State for menu toggles
	const [navbarOpen, setNavbarOpen] = useState(false)

	// Template — mirrors the Django (Tailgrids) `#tailgrids-navbar` markup.
	return (
		<>
			<div className="bg-white">
				<div className="container mx-auto">
					<div className="relative -mx-4 flex items-center justify-between">
						{/* Logo cell */}
						<div className="w-60 max-w-full px-4">
							<a
								href={API_URL}
								className="block h-full py-5"
								aria-label={t("common:web.homepage")}
							>
								<BrandLogo />
							</a>
						</div>

						{/* Right cell: nav + login/signup */}
						<div className="z-50 flex w-full items-center justify-between px-4">
							<div>
								{/* Mobile hamburger */}
								<button
									type="button"
									className="absolute top-1/2 right-4 block -translate-y-1/2 rounded-lg px-3 py-[6px] lg:hidden"
									onClick={() => setNavbarOpen(!navbarOpen)}
									aria-expanded={navbarOpen}
									aria-label={t(
										"common:navigation.open_sidebar.sr_text",
									)}
									data-testid="navigation.open_sidebar"
								>
									<MenuIcon
										isOpen={true}
										aria-hidden="true"
										className="h-3 w-6"
									/>
								</button>

								{/* Desktop nav */}
								<nav
									aria-label={t("common:web.homepage")}
									className="hidden lg:static lg:block lg:w-full lg:max-w-full"
								>
									<ul className="block lg:flex">
										{navigation?.map((item) =>
											item.has_submenu ? (
												<DesktopNavItemWithSubMenu
													key={item.title}
													item={item}
												/>
											) : (
												<li key={item.title}>
													<a
														href={item.url}
														className="hover:text-secondary-500 flex py-2 text-base font-normal lg:ml-12 lg:inline-flex"
														target={
															item.open_in_new_tab
																? "_blank"
																: undefined
														}
														rel={
															item.open_in_new_tab
																? "noreferrer"
																: undefined
														}
													>
														{item.title}
													</a>
												</li>
											),
										)}
									</ul>
								</nav>
							</div>

							{/* Login / Signup — desktop only. */}
							<div className="hidden items-center justify-end gap-3 pr-16 lg:flex lg:pr-0">
								<ButtonLink
									href={loginUrl}
									variant="primary"
									size="pill"
								>
									{t("common:nav.login")}
								</ButtonLink>
								<ButtonLink
									href={SIGNUP_URL}
									variant="pill-secondary"
									size="pill"
								>
									{t("common:nav.signup")}
								</ButtonLink>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Mobile slide-in navigation */}
			<Transition show={navbarOpen} as={Fragment}>
				<Dialog
					as="div"
					className="lg:hidden"
					onClose={() => setNavbarOpen(false)}
				>
					<div className="fixed inset-x-0 top-(--preview-bar-height) bottom-0 z-40 flex">
						<TransitionChild as={Fragment}>
							<DialogBackdrop
								className={cn(
									"fixed inset-0 bg-gray-600/75",
									"transition duration-300 ease-in-out data-closed:opacity-0",
								)}
							/>
						</TransitionChild>
						<TransitionChild as={Fragment}>
							<div
								className={cn(
									"relative flex w-full max-w-[256px] flex-1 flex-col bg-white",
									"transition duration-300 ease-in-out data-closed:-translate-x-full sm:max-w-sm",
								)}
							>
								<TransitionChild as={Fragment}>
									<div
										className={cn(
											"absolute top-1 right-0 -mr-14 p-1 transition duration-300 ease-in-out data-closed:opacity-0",
											"data-closed:-translate-x-full",
										)}
									>
										<button
											type="button"
											className="flex size-12 items-center justify-center"
											onClick={() => setNavbarOpen(false)}
										>
											<MenuIcon
												className="size-6"
												aria-hidden="true"
												isOpen={false}
											/>
											<span className="sr-only">
												{t(
													"common:navigation.close_sidebar.sr_text",
												)}
											</span>
										</button>
									</div>
								</TransitionChild>
								<div className="flex h-[calc(100vh-var(--preview-bar-height))] flex-1 flex-col overflow-y-auto">
									<div
										className={cn(
											"bg-primary-500 flex h-14 shrink-0 items-center shadow-[0px_1px_3px_rgba(0,0,0,0.25)] 2xl:h-16",
											"pl-2",
										)}
									>
										<a
											href={API_URL}
											className="block h-full py-3"
											aria-label={t(
												"common:web.homepage",
											)}
										>
											<BrandLogo />
										</a>
									</div>
									{/* Navigation items */}
									<div className="flex-1">
										<div className="space-y-1 p-4">
											{navigation?.map((item) =>
												item.has_submenu ? (
													<MobileNavItemWithSubMenu
														key={item.title}
														item={item}
														onItemClick={() =>
															setNavbarOpen(false)
														}
													/>
												) : (
													<a
														key={item.title}
														href={item.url}
														className="block rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
														target={
															item.open_in_new_tab
																? "_blank"
																: undefined
														}
														rel={
															item.open_in_new_tab
																? "noreferrer"
																: undefined
														}
														onClick={() =>
															setNavbarOpen(false)
														}
													>
														{item.title}
													</a>
												),
											)}
										</div>
									</div>

									{/* Footer with Login / Signup */}
									<div className="shrink-0 space-y-3 border-t border-gray-200 py-4">
										<div className="space-y-3 px-4">
											<ButtonLink
												href={loginUrl}
												variant="primary"
												size="pill"
												className="w-full justify-center"
											>
												{t("common:nav.login")}
											</ButtonLink>
											<ButtonLink
												href={SIGNUP_URL}
												variant="pill-secondary"
												size="pill"
												className="w-full justify-center"
											>
												{t("common:nav.signup")}
											</ButtonLink>
										</div>
									</div>
								</div>
							</div>
						</TransitionChild>
						<div className="w-14 shrink-0" aria-hidden="true">
							{/* Dummy element to force sidebar to shrink to fit close icon */}
						</div>
					</div>
				</Dialog>
			</Transition>
		</>
	)
}

/**
 * DesktopNavItemWithSubMenu
 */
function DesktopNavItemWithSubMenu({ item }: { item: NavigationItemType }) {
	const [showSubMenu, setShowSubMenu] = useState(false)

	return (
		// Close on keyboard tab-away; pointer clicks are handled below.
		// oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
		<li
			className="relative"
			onBlur={(e) => {
				if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
					setShowSubMenu(false)
				}
			}}
		>
			<button
				className="hover:text-secondary-500 flex w-full items-center justify-between gap-2 py-2 text-base font-normal lg:ml-12 lg:inline-flex lg:w-auto lg:justify-center"
				onClick={() => setShowSubMenu(!showSubMenu)}
				aria-expanded={showSubMenu}
				aria-haspopup="true"
			>
				{item.title}
				<span
					aria-hidden="true"
					className={cn(
						"duration-200",
						showSubMenu ? "-scale-y-100" : "",
					)}
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
					>
						<path /* oxlint-disable-next-line max-len */
							d="M10 14.25C9.8125 14.25 9.65625 14.1875 9.5 14.0625L2.3125 7C2.03125 6.71875 2.03125 6.28125 2.3125 6C2.59375 5.71875 3.03125 5.71875 3.3125 6L10 12.5312L16.6875 5.9375C16.9688 5.65625 17.4062 5.65625 17.6875 5.9375C17.9688 6.21875 17.9688 6.65625 17.6875 6.9375L10.5 14C10.3437 14.1562 10.1875 14.25 10 14.25Z"
							fill="currentColor"
						/>
					</svg>
				</span>
			</button>
			{/*
			  preventDefault on mousedown stops the button from blurring
			  (which would hide this wrapper before mouseup lands on the
			  link), so submenu clicks navigate on Safari/Firefox-on-macOS.
			*/}
			{/* oxlint-disable-next-line jsx-a11y/no-static-element-interactions */}
			<div
				onMouseDown={(e) => e.preventDefault()}
				className={cn(
					"z-50 w-full rounded-xl bg-white p-2 lg:absolute lg:top-full lg:left-0 lg:m-8 lg:w-[630px] lg:p-8 lg:shadow-lg",
					showSubMenu ? "block" : "hidden",
				)}
			>
				<div className="grid gap-5 lg:auto-cols-max lg:grid-flow-col lg:justify-between">
					{item.sub_menu_headers.map((header) => (
						<div key={header.title}>
							<h4 className="mb-3 text-sm font-bold">
								{header.title}
							</h4>
							<div className="flex flex-col space-y-3">
								{header.sub_menu_items.map((subitem) => (
									<a
										key={subitem.title}
										href={subitem.url}
										className={cn(
											"hover:text-secondary-500 text-base font-normal",
											BODY_COLOR_CLASS,
										)}
										target={
											subitem.open_in_new_tab
												? "_blank"
												: undefined
										}
										rel={
											subitem.open_in_new_tab
												? "noreferrer"
												: undefined
										}
									>
										{subitem.title}
									</a>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</li>
	)
}

/**
 * MobileNavItemWithSubMenu
 */
function MobileNavItemWithSubMenu({
	item,
	onItemClick,
}: {
	item: NavigationItemType
	onItemClick: () => void
}) {
	const [showSubMenu, setShowSubMenu] = useState(false)

	return (
		<div className="space-y-1">
			<button
				className="flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
				onClick={() => setShowSubMenu(!showSubMenu)}
				aria-expanded={showSubMenu}
			>
				{item.title}
				<svg
					aria-hidden="true"
					className={cn(
						"ml-2 size-5 transition-transform duration-200",
						showSubMenu ? "rotate-180" : "",
					)}
					fill="none"
					viewBox="0 0 20 20"
				>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="1.5"
						d="M6 8l4 4 4-4"
					/>
				</svg>
			</button>
			{showSubMenu && (
				<div className="space-y-2 pl-4">
					{item.sub_menu_headers.map((header) => (
						<div key={header.title} className="space-y-2">
							<div className="space-y-1">
								{header.sub_menu_items.map((subitem) => (
									<a
										key={subitem.title}
										href={subitem.url}
										className="block rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
										target={
											subitem.open_in_new_tab
												? "_blank"
												: undefined
										}
										rel={
											subitem.open_in_new_tab
												? "noreferrer"
												: undefined
										}
										onClick={onItemClick}
									>
										{subitem.title}
									</a>
								))}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
}
